
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, {
  PROJECT_STATUSES, SIGN_TYPES, FONT_OPTIONS, PRESET_COLORS, statusTone,
} from "@/lib/api";
import AppShell from "@/components/AppShell";
import MockupCanvas from "@/components/MockupCanvas";
import { WS } from "@/constants/testIds";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ArrowLeft, Save, FileDown, Upload, Image as ImageIcon,
  Sun, Moon, Sparkles, Wand2, Lock, AlignCenter, Maximize2, Download
} from "lucide-react";

const DEFAULT_PROJECT = null;

function countLetters(text, excludeSpaces, excludePunct) {
  let t = text || "";
  if (excludeSpaces) t = t.replace(/\s/g, "");
  if (excludePunct) t = t.replace(/[^\p{L}\p{N}]/gu, "");
  return t.length;
}

function recalcPricing(sign, pricing) {
  const base = (sign.letter_height_in || 0) * (sign.letter_count || 0) * (pricing.price_per_inch || 0);
  const subtotal =
    base + (pricing.installation || 0) + (pricing.permit || 0) +
    (pricing.electrical || 0) + (pricing.raceway || 0) + (pricing.removal || 0) +
    (pricing.misc || 0) - (pricing.discount || 0);
  const total = subtotal + (pricing.tax || 0);
  return { ...pricing, base_letter_cost: base, subtotal, total };
}

export default function Workspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(DEFAULT_PROJECT);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingMockup, setExportingMockup] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load project
  
  useEffect(() => {
    
    (async () => {
      try {
        const { data } = await api.get(`/projects/${id}`);
        setProject(data);
      }
      catch { toast.error("Project not found"); nav("/dashboard"); }
    })();
  }, [id, nav]);

  // Derived: auto-counted letters and live pricing computed during render
  const autoLetterCount = useMemo(() => {
    if (!project) return 0;
    return countLetters(
      project.sign.main_text,
      project.sign.exclude_spaces,
      project.sign.exclude_punctuation,
    );
  }, [project?.sign?.main_text, project?.sign?.exclude_spaces, project?.sign?.exclude_punctuation]);

  const livePricing = useMemo(() => {
    if (!project) return null;
    const signWithCount = { ...project.sign, letter_count: project.sign.letter_count ?? autoLetterCount };
    return recalcPricing(signWithCount, project.pricing);
  }, [project, autoLetterCount]);

  if (!project) {
    return (
      <AppShell>
        <div className="text-zinc-500 text-sm tracking-widest uppercase animate-pulse">Loading project…</div>
      </AppShell>
    );
  }

  const update = (patch) => setProject(p => ({ ...p, ...patch }));
  const updateSign = (patch) => setProject(p => {
    const newSign = { ...p.sign, ...patch };
    // Auto-update letter_count if main_text or exclusion toggles changed (unless user explicitly changed letter_count itself)
    if (("main_text" in patch) || ("exclude_spaces" in patch) || ("exclude_punctuation" in patch)) {
      newSign.letter_count = countLetters(newSign.main_text, newSign.exclude_spaces, newSign.exclude_punctuation);
    }
    return { ...p, sign: newSign };
  });
  const updatePricing = (patch) => setProject(p => ({ ...p, pricing: { ...p.pricing, ...patch } }));
  const updateMockup = (patch) => setProject(p => ({ ...p, mockup_config: { ...p.mockup_config, ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_name: project.customer_name,
        business_name: project.business_name,
        phone: project.phone, email: project.email, address: project.address,
        status: project.status,
        client_notes: project.client_notes, internal_notes: project.internal_notes,
        sign: project.sign, pricing: livePricing, mockup_config: project.mockup_config,
      };
      const { data } = await api.put(`/projects/${id}`, payload);
      setProject(data);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${id}/storefront`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProject(p => ({ ...p, storefront_path: data.storefront_path, storefront_mime: data.storefront_mime }));
      toast.success("Storefront uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      // Save first to ensure backend has latest data + render mockup snapshot
      await save();
      // Composite & upload mockup as PNG, then trigger PDF download
      try {
        if (project.storefront_path && project.sign.main_text) {
          const blob = await canvasRef.current.exportPng();
          const fd = new FormData();
          fd.append("file", blob, "mockup.png");
          await api.post(`/projects/${id}/mockup`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
      } catch (err) {
        console.warn("mockup snapshot failed", err);
      }
      const res = await api.get(`/projects/${id}/proposal.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SignFlow360-${(project.business_name || "proposal").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Proposal exported");
    } catch { toast.error("PDF export failed"); }
    finally { setExporting(false); }
  };

  const exportMockup = async () => {
    if (!project.storefront_path || !project.sign.main_text) {
      toast.error("Upload a photo and enter sign text first");
      return;
    }
    setExportingMockup(true);
    try {
      const blob = await canvasRef.current.exportPng();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mockup-${(project.business_name || "sign").replace(/\s+/g, "_")}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Mockup saved");
    } catch { toast.error("Mockup export failed"); }
    finally { setExportingMockup(false); }
  };

  const sign = project.sign;
  const pricing = project.pricing;
  const mockup = project.mockup_config;
  const lp = livePricing || pricing;

  return (
    <AppShell right={
      <div className="flex items-center gap-2">
        <Button data-testid={WS.saveBtn} variant="outline" size="sm" onClick={save} disabled={saving}
          className="border-zinc-800 h-9 gap-1.5">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
        </Button>
        <Button data-testid={WS.exportPdfBtn} size="sm" onClick={exportPdf} disabled={exporting}
          className="bg-brand hover:bg-brand-hover h-9 gap-1.5">
          <FileDown className="w-3.5 h-3.5" /> {exporting ? "Exporting…" : "Export PDF"}
        </Button>
      </div>
    }>
      <div data-testid={WS.page} className="enter-up">
        {/* Top bar */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <button data-testid={WS.backBtn} onClick={() => nav("/dashboard")}
              className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white tracking-widest uppercase">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to projects
            </button>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">
              {project.business_name || "Untitled project"}
            </h1>
            <div className="text-xs text-zinc-500 mt-1 font-mono">ID {project.id.slice(0, 8).toUpperCase()}</div>
          </div>
          <span className={`hidden sm:inline-flex text-[10px] tracking-[0.18em] uppercase font-semibold border rounded-sm px-2 py-1 ${statusTone(project.status)}`}>
            {project.status}
          </span>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left sidebar */}
          <aside className="col-span-12 lg:col-span-4 space-y-4">
            <Tabs defaultValue="customer">
              <TabsList className="grid grid-cols-3 bg-[#0c0c0e] border border-zinc-800 h-9">
                <TabsTrigger value="customer">Customer</TabsTrigger>
                <TabsTrigger value="sign">Sign</TabsTrigger>
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
              </TabsList>

              <TabsContent value="customer" className="surface-card rounded-md p-5 mt-3 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-tag">Business *</Label>
                    <Input data-testid={WS.businessName} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                      value={project.business_name} onChange={(e) => update({ business_name: e.target.value })} />
                  </div>
                  <div>
                    <Label className="label-tag">Customer</Label>
                    <Input data-testid={WS.customerName} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                      value={project.customer_name} onChange={(e) => update({ customer_name: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-tag">Phone</Label>
                    <Input data-testid={WS.phone} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                      value={project.phone} onChange={(e) => update({ phone: e.target.value })} />
                  </div>
                  <div>
                    <Label className="label-tag">Email</Label>
                    <Input data-testid={WS.email} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                      value={project.email} onChange={(e) => update({ email: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="label-tag">Address</Label>
                  <Input data-testid={WS.address} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                    value={project.address} onChange={(e) => update({ address: e.target.value })} />
                </div>
                <div>
                  <Label className="label-tag">Status</Label>
                  <Select value={project.status} onValueChange={(v) => update({ status: v })}>
                    <SelectTrigger data-testid={WS.status} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROJECT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="sign" className="surface-card rounded-md p-5 mt-3 space-y-4">
                <div>
                  <Label className="label-tag">Sign Type</Label>
                  <Select value={sign.sign_type} onValueChange={(v) => updateSign({ sign_type: v })}>
                    <SelectTrigger data-testid={WS.signType} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {SIGN_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="label-tag">Main Sign Text</Label>
                  <Input data-testid={WS.mainText} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                    value={sign.main_text} placeholder="e.g. Tina's Mexican Grill"
                    onChange={(e) => updateSign({ main_text: e.target.value })} />
                </div>
                <div>
                  <Label className="label-tag">Secondary Text (optional)</Label>
                  <Input data-testid={WS.secondaryText} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                    value={sign.secondary_text}
                    onChange={(e) => updateSign({ secondary_text: e.target.value })} />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Switch data-testid={WS.excludeSpaces} checked={sign.exclude_spaces} onCheckedChange={(v) => updateSign({ exclude_spaces: v })} />
                    <span className="text-zinc-300">Exclude spaces</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch data-testid={WS.excludePunct} checked={sign.exclude_punctuation} onCheckedChange={(v) => updateSign({ exclude_punctuation: v })} />
                    <span className="text-zinc-300">Exclude punct.</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-tag">Letter Count</Label>
                    <Input data-testid={WS.letterCount} type="number" className="mt-1.5 bg-[#0c0c0e] border-zinc-800 font-mono"
                      value={sign.letter_count}
                      onChange={(e) => updateSign({ letter_count: parseInt(e.target.value || "0", 10) })} />
                    <div className="text-[10px] text-zinc-600 mt-1">Auto-counted; override if needed.</div>
                  </div>
                  <div>
                    <Label className="label-tag">Letter Height (in)</Label>
                    <Input data-testid={WS.letterHeight} type="number" step="0.5" className="mt-1.5 bg-[#0c0c0e] border-zinc-800 font-mono"
                      value={sign.letter_height_in}
                      onChange={(e) => updateSign({ letter_height_in: parseFloat(e.target.value || "0") })} />
                  </div>
                </div>

                <div className="border-t border-zinc-800 pt-4">
                  <div className="label-tag mb-2">Sign Area (available)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] text-zinc-500">Width (in)</Label>
                      <Input data-testid={WS.areaWidthIn} type="number" className="mt-1 bg-[#0c0c0e] border-zinc-800 font-mono"
                        value={sign.area_width_in}
                        onChange={(e) => updateSign({ area_width_in: parseFloat(e.target.value || "0") })} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-zinc-500">Height (in)</Label>
                      <Input data-testid={WS.areaHeightIn} type="number" className="mt-1 bg-[#0c0c0e] border-zinc-800 font-mono"
                        value={sign.area_height_in}
                        onChange={(e) => updateSign({ area_height_in: parseFloat(e.target.value || "0") })} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-zinc-500">Width (ft)</Label>
                      <Input data-testid={WS.areaWidthFt} type="number" step="0.1" className="mt-1 bg-[#0c0c0e] border-zinc-800 font-mono"
                        value={+(sign.area_width_in / 12).toFixed(2)}
                        onChange={(e) => updateSign({ area_width_in: parseFloat(e.target.value || "0") * 12 })} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-zinc-500">Height (ft)</Label>
                      <Input data-testid={WS.areaHeightFt} type="number" step="0.1" className="mt-1 bg-[#0c0c0e] border-zinc-800 font-mono"
                        value={+(sign.area_height_in / 12).toFixed(2)}
                        onChange={(e) => updateSign({ area_height_in: parseFloat(e.target.value || "0") * 12 })} />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="pricing" className="surface-card rounded-md p-5 mt-3 space-y-3">
                <PricingRow id={WS.pricePerInch} label="Price per inch" prefix="$" value={pricing.price_per_inch} onChange={(v) => updatePricing({ price_per_inch: v })} />
                <div className="border-t border-zinc-800 pt-3">
                  <div className="text-xs flex items-center justify-between">
                    <span className="text-zinc-400">Base letter cost</span>
                    <span className="font-mono">${(lp.base_letter_cost || 0).toFixed(2)}</span>
                  </div>
                  <div className="text-[10px] text-zinc-600 mt-1">{sign.letter_height_in} × {sign.letter_count} × ${pricing.price_per_inch}</div>
                </div>
                <PricingRow id={WS.installation} label="Installation" value={pricing.installation} onChange={(v) => updatePricing({ installation: v })} />
                <PricingRow id={WS.permit} label="Permit" value={pricing.permit} onChange={(v) => updatePricing({ permit: v })} />
                <PricingRow id={WS.electrical} label="Electrical" value={pricing.electrical} onChange={(v) => updatePricing({ electrical: v })} />
                <PricingRow id={WS.raceway} label="Raceway" value={pricing.raceway} onChange={(v) => updatePricing({ raceway: v })} />
                <PricingRow id={WS.removal} label="Removal of old sign" value={pricing.removal} onChange={(v) => updatePricing({ removal: v })} />
                <PricingRow id={WS.misc} label="Miscellaneous" value={pricing.misc} onChange={(v) => updatePricing({ misc: v })} />
                <PricingRow id={WS.discount} label="Discount (−)" value={pricing.discount} onChange={(v) => updatePricing({ discount: v })} />
                <PricingRow id={WS.tax} label="Tax (optional)" value={pricing.tax} onChange={(v) => updatePricing({ tax: v })} />
              </TabsContent>
            </Tabs>

            {/* Notes */}
            <div className="surface-card rounded-md p-5">
              <div className="label-tag mb-2">Customer-facing notes</div>
              <Textarea data-testid={WS.clientNotes} rows={3} className="bg-[#0c0c0e] border-zinc-800"
                value={project.client_notes || ""}
                onChange={(e) => update({ client_notes: e.target.value })}
                placeholder="Anything you want shown on the proposal…" />
              <div className="label-tag mt-4 mb-2 text-amber-500/90">Internal notes — not in PDF</div>
              <Textarea data-testid={WS.internalNotes} rows={3} className="bg-[#0c0c0e] border-zinc-800"
                value={project.internal_notes || ""}
                onChange={(e) => update({ internal_notes: e.target.value })}
                placeholder="Waiting on landlord approval, verify permit, customer wants halo-lit…" />
            </div>
          </aside>

          {/* Main canvas + controls */}
          <section className="col-span-12 lg:col-span-8 space-y-4">
            {/* Upload + AI placeholders */}
            <div className="flex flex-col md:flex-row gap-3">
              <label
                data-testid={WS.uploadDropzone}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleUpload(f);
                }}
                className="flex-1 cursor-pointer surface-card rounded-md p-4 flex items-center gap-3 hover:border-brand transition-colors"
              >
                <div className="h-10 w-10 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center text-brand">
                  <Upload className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {project.storefront_path ? "Replace storefront photo" : "Upload storefront photo"}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    Drag & drop or click — JPG, PNG, HEIC supported
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  data-testid={WS.uploadInput}
                  type="file"
                  accept="image/*,.heic,.heif"
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                />
                <span className="label-tag text-brand">{uploading ? "Uploading…" : "Browse"}</span>
              </label>

              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid={WS.aiVisionBtn}
                      disabled
                      className="surface-card rounded-md p-4 flex items-center gap-3 opacity-50 cursor-not-allowed min-w-[200px]"
                    >
                      <Sparkles className="w-4 h-4 text-brand" />
                      <div className="text-left">
                        <div className="text-sm font-semibold">AI Vision</div>
                        <div className="text-[10px] tracking-widest text-zinc-500 uppercase flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Coming Soon</div>
                      </div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-zinc-900 border-zinc-800">
                    <div className="text-xs max-w-[220px]">
                      <b className="text-white">AI Vision Analysis</b>
                      <ul className="mt-1 text-zinc-400 list-disc list-inside text-[11px]">
                        <li>Detect storefront sign area</li>
                        <li>Detect building dimensions</li>
                        <li>Suggest optimal sign size</li>
                        <li>Detect existing signage</li>
                      </ul>
                    </div>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid={WS.aiMockupBtn}
                      disabled
                      className="surface-card rounded-md p-4 flex items-center gap-3 opacity-50 cursor-not-allowed min-w-[200px]"
                    >
                      <Wand2 className="w-4 h-4 text-brand" />
                      <div className="text-left">
                        <div className="text-sm font-semibold">AI Mockup</div>
                        <div className="text-[10px] tracking-widest text-zinc-500 uppercase flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Coming Soon</div>
                      </div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-zinc-900 border-zinc-800">
                    <div className="text-xs max-w-[240px]">
                      <b className="text-white">AI Mockup Generator</b>
                      <ul className="mt-1 text-zinc-400 list-disc list-inside text-[11px]">
                        <li>Realistic daytime mockups</li>
                        <li>Illuminated nighttime mockups</li>
                        <li>Front-lit & halo-lit letters</li>
                        <li>Raceway & flush-mounted</li>
                      </ul>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Mockup canvas */}
            <MockupCanvas
              ref={canvasRef}
              storefrontPath={project.storefront_path}
              signText={sign.main_text}
              mockup={mockup}
              onChange={(m) => setProject(p => ({ ...p, mockup_config: m }))}
              night={mockup.night_mode}
            />

            {/* Mockup toolbar */}
            <div className="surface-card rounded-md p-4">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-12 md:col-span-3">
                  <Label className="label-tag">Font</Label>
                  <Select value={mockup.font_family} onValueChange={(v) => updateMockup({ font_family: v })}>
                    <SelectTrigger data-testid={WS.fontSelect} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-12 md:col-span-4">
                  <Label className="label-tag">Color</Label>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    {PRESET_COLORS.map(c => (
                      <button key={c} onClick={() => updateMockup({ color: c })}
                        className={`w-6 h-6 rounded-sm border ${mockup.color === c ? "border-brand ring-2 ring-brand/40" : "border-zinc-700"}`}
                        style={{ background: c }} aria-label={c} />
                    ))}
                    <input data-testid={WS.colorPicker} type="color" value={mockup.color}
                      onChange={(e) => updateMockup({ color: e.target.value })}
                      className="w-7 h-7 bg-transparent border border-zinc-700 rounded-sm cursor-pointer" />
                  </div>
                </div>

                <div className="col-span-6 md:col-span-2">
                  <Label className="label-tag">Spacing</Label>
                  <Slider data-testid={WS.letterSpacing} className="mt-3"
                    value={[mockup.letter_spacing || 0]} min={-5} max={20} step={1}
                    onValueChange={([v]) => updateMockup({ letter_spacing: v })} />
                </div>

                <div className="col-span-6 md:col-span-2">
                  <Label className="label-tag">Rotation</Label>
                  <Slider data-testid={WS.rotation} className="mt-3"
                    value={[mockup.rotation || 0]} min={-15} max={15} step={1}
                    onValueChange={([v]) => updateMockup({ rotation: v })} />
                </div>

                <div className="col-span-12 md:col-span-1 flex items-end justify-end gap-2">
                  <button
                    data-testid={WS.dayNightToggle}
                    onClick={() => updateMockup({ night_mode: !mockup.night_mode })}
                    className={`h-9 px-3 rounded-sm border text-sm inline-flex items-center gap-2 transition-colors ${
                      mockup.night_mode ? "border-brand text-brand bg-brand/10" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                    }`}
                  >
                    {mockup.night_mode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                    {mockup.night_mode ? "Night" : "Day"}
                  </button>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-3">
                <ToggleChip checked={mockup.shadow} onClick={() => updateMockup({ shadow: !mockup.shadow })} testid={WS.shadowToggle}>Shadow</ToggleChip>
                <ToggleChip checked={mockup.glow} onClick={() => updateMockup({ glow: !mockup.glow })} testid={WS.glowToggle}>Glow</ToggleChip>
                <div className="h-5 border-l border-zinc-800 mx-1" />
                <button data-testid={WS.fitWidth} onClick={() => canvasRef.current?.fitWidth()}
                  className="text-xs h-8 px-3 border border-zinc-800 hover:border-zinc-600 rounded-sm inline-flex items-center gap-1.5">
                  <Maximize2 className="w-3 h-3" /> Fit
                </button>
                <button data-testid={WS.centerText} onClick={() => canvasRef.current?.centerText()}
                  className="text-xs h-8 px-3 border border-zinc-800 hover:border-zinc-600 rounded-sm inline-flex items-center gap-1.5">
                  <AlignCenter className="w-3 h-3" /> Center
                </button>
                <div className="flex-1" />
                <Button data-testid={WS.exportMockupBtn} size="sm" variant="outline"
                  onClick={exportMockup} disabled={exportingMockup}
                  className="border-zinc-800 h-8 gap-1.5">
                  <Download className="w-3.5 h-3.5" />
                  {exportingMockup ? "Saving…" : "Download mockup"}
                </Button>
              </div>

              {/* Mockup dimension controls */}
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-tag">Sign width %</Label>
                  <Slider className="mt-3" value={[mockup.width_pct]} min={10} max={100} step={1}
                    onValueChange={([v]) => updateMockup({ width_pct: v })} />
                </div>
                <div>
                  <Label className="label-tag">Sign height %</Label>
                  <Slider className="mt-3" value={[mockup.height_pct]} min={4} max={50} step={1}
                    onValueChange={([v]) => updateMockup({ height_pct: v })} />
                </div>
              </div>
            </div>

            {/* Quote summary */}
            <div className="surface-card rounded-md p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="label-tag">Quote Summary</div>
                  <h3 className="font-heading text-xl font-bold tracking-tight">{project.business_name || "Untitled"}</h3>
                </div>
                <div className="text-right">
                  <div className="label-tag">Total Estimate</div>
                  <div data-testid={WS.total} className="font-mono text-3xl font-bold text-brand">
                    ${(lp.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Summary k="Sign Type" v={sign.sign_type} />
                <Summary k="Letter Height" v={`${sign.letter_height_in}"`} mono />
                <Summary k="Letter Count" v={sign.letter_count} mono />
                <Summary k="Price / inch" v={`$${pricing.price_per_inch}`} mono />
                <Summary k="Base Letters" v={`$${(lp.base_letter_cost || 0).toFixed(2)}`} mono />
                <Summary k="Installation" v={`$${(pricing.installation || 0).toFixed(2)}`} mono />
                <Summary k="Permit + Electrical" v={`$${((pricing.permit || 0) + (pricing.electrical || 0)).toFixed(2)}`} mono />
                <Summary k="Subtotal" v={`$${(lp.subtotal || 0).toFixed(2)}`} mono />
              </div>
              <div className="mt-4 text-[10px] text-zinc-600 leading-relaxed border-t border-zinc-800 pt-3">
                This is a preliminary estimate based on the information provided. Final pricing may vary after site inspection, city permit requirements, electrical requirements, landlord approval, production method, and final measurements.
              </div>
            </div>
          </section>
        </div>
      </div>
    </AppShell>
  );
}

function PricingRow({ id, label, value, onChange, prefix = "$" }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label className="text-xs text-zinc-400 flex-1">{label}</Label>
      <div className="relative w-36">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">{prefix}</span>
        <Input data-testid={id} type="number" step="0.01"
          className="bg-[#0c0c0e] border-zinc-800 pl-6 h-8 text-sm font-mono"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value || "0") || 0)} />
      </div>
    </div>
  );
}

function Summary({ k, v, mono }) {
  return (
    <div>
      <div className="label-tag text-[9px]">{k}</div>
      <div className={`mt-0.5 ${mono ? "font-mono" : ""}`}>{v}</div>
    </div>
  );
}

function ToggleChip({ checked, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`text-xs h-8 px-3 rounded-sm border inline-flex items-center gap-1.5 transition-colors ${
        checked ? "border-brand text-brand bg-brand/10" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
      }`}>
      {children}
    </button>
  );
}
